import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Star, Check, X, Loader2, Search } from "lucide-react";

// State FIPS -> USPS abbrev (matches the shared Census dataset's `state` field).
const TIER_LABEL = { primary: "Primary", also: "Also-willing" };

export const NotaryServiceCounties = ({
  getUrl = "/provider/service-counties",
  putUrl = "/provider/service-counties",
  title = "Notary service counties",
  subtitle,
  onSaved,
} = {}) => {
  const [selected, setSelected] = useState({}); // fips -> {state, county_name, county_fips, tier}
  const [dataset, setDataset] = useState(null); // lazy-loaded county list
  const [loadingDataset, setLoadingDataset] = useState(false);
  const [stateSel, setStateSel] = useState("");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(getUrl).then(({ data }) => {
      const m = {};
      (data.counties || []).forEach((c) => { m[c.county_fips] = c; });
      setSelected(m);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [getUrl]);

  // Lazy-load (code-split) the ~3,200-county dataset only when the picker is first opened.
  const ensureDataset = async () => {
    if (dataset || loadingDataset) return;
    setLoadingDataset(true);
    try {
      const mod = await import("@/data/usCounties.json");
      setDataset((mod.default || mod).counties || []);
    } catch {
      toast.error("Could not load the county list. Please try again.");
    } finally {
      setLoadingDataset(false);
    }
  };

  const states = useMemo(() => {
    if (!dataset) return [];
    return Array.from(new Set(dataset.map((c) => c.state))).sort();
  }, [dataset]);

  const countiesForState = useMemo(() => {
    if (!dataset || !stateSel) return [];
    const q = query.trim().toLowerCase();
    return dataset.filter((c) => c.state === stateSel && (!q || c.name.toLowerCase().includes(q)));
  }, [dataset, stateSel, query]);

  const setTier = (county, tier) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (!tier) delete next[county.fips];
      else next[county.fips] = { state: county.state, county_name: county.name, county_fips: county.fips, tier };
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { counties: Object.values(selected).map((c) => ({
        state: c.state, county_name: c.county_name, county_fips: c.county_fips, tier: c.tier })) };
      const { data } = await api.put(putUrl, payload);
      const m = {};
      (data.counties || []).forEach((c) => { m[c.county_fips] = c; });
      setSelected(m);
      toast.success("Service counties saved");
      if (onSaved) onSaved(data.counties || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save counties");
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const g = { primary: [], also: [] };
    Object.values(selected).forEach((c) => { (g[c.tier] || (g[c.tier] = [])).push(c); });
    g.primary.sort((a, b) => (a.state + a.county_name).localeCompare(b.state + b.county_name));
    g.also.sort((a, b) => (a.state + a.county_name).localeCompare(b.state + b.county_name));
    return g;
  }, [selected]);

  const total = Object.keys(selected).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="notary-counties-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <MapPin className="h-5 w-5 text-indigo-500" /> {title}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {subtitle || (<>Optional. Tell us the counties you cover so notary jobs there reach you first. Leaving this
            empty means <span className="font-medium text-slate-600">no preference</span> — you'll still
            see and can claim any notary job.</>)}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600" data-testid="notary-counties-total">{total} selected</span>
      </div>

      {/* Selected summary */}
      {total > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {(["primary", "also"]).map((tier) => (
            <div key={tier} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`notary-counties-group-${tier}`}>
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {tier === "primary" ? <Star className="h-3.5 w-3.5 text-amber-500" /> : <Check className="h-3.5 w-3.5 text-indigo-500" />}
                {TIER_LABEL[tier]} ({grouped[tier].length})
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {grouped[tier].length === 0 && <span className="text-xs text-slate-400">None yet</span>}
                {grouped[tier].map((c) => (
                  <span key={c.county_fips} data-testid={`notary-county-chip-${c.county_fips}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">
                    {c.county_name}, {c.state}
                    <button data-testid={`notary-county-remove-${c.county_fips}`} onClick={() => setTier({ fips: c.county_fips }, null)} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Picker */}
      <div className="mt-4 rounded-lg border border-slate-200 p-3">
        {!dataset ? (
          <Button variant="outline" data-testid="notary-counties-open" disabled={loadingDataset} onClick={ensureDataset}>
            {loadingDataset ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading counties…</> : "Add / edit counties"}
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">State</Label>
                <select data-testid="notary-counties-state" value={stateSel} onChange={(e) => { setStateSel(e.target.value); setQuery(""); }} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">Select a state…</option>
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {stateSel && (
                <div>
                  <Label className="text-xs">Find a county</Label>
                  <div className="relative mt-1">
                    <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input data-testid="notary-counties-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Type to filter…" className="pl-8" />
                  </div>
                </div>
              )}
            </div>

            {stateSel && (
              <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-100" data-testid="notary-counties-list">
                {countiesForState.map((c) => {
                  const cur = selected[c.fips]?.tier;
                  return (
                    <div key={c.fips} data-testid={`notary-county-row-${c.fips}`} className="flex items-center justify-between px-3 py-2">
                      <span className="text-sm text-slate-700">{c.name}</span>
                      <div className="flex gap-1.5">
                        <button data-testid={`notary-county-primary-${c.fips}`} onClick={() => setTier(c, cur === "primary" ? null : "primary")}
                          className={`rounded-md px-2 py-1 text-xs font-semibold transition ${cur === "primary" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-amber-100"}`}>Primary</button>
                        <button data-testid={`notary-county-also-${c.fips}`} onClick={() => setTier(c, cur === "also" ? null : "also")}
                          className={`rounded-md px-2 py-1 text-xs font-semibold transition ${cur === "also" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-indigo-100"}`}>Also-willing</button>
                      </div>
                    </div>
                  );
                })}
                {countiesForState.length === 0 && <p className="px-3 py-4 text-center text-sm text-slate-400">No counties match.</p>}
              </div>
            )}
          </div>
        )}
      </div>

      <Button data-testid="notary-counties-save" onClick={save} disabled={saving || !loaded} className="mt-4 bg-slate-900 text-white hover:bg-slate-800">
        {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Save counties"}
      </Button>
    </div>
  );
};
